import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { User as UserIcon, Mail, MapPin, Phone, ShieldCheck, KeyRound, Camera } from "lucide-react";
import { toast } from "sonner";

import { patch, post } from "@/lib/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useAuthStore } from "@/store/auth-store";
import type { User } from "@/types/auth";

interface ProfileForm { 
  full_name: string; 
  email: string; 
  address: string; 
  profile_image?: FileList; 
}
interface PwdForm { 
  old_password: string; 
  new_password: string; 
  new_password_confirm: string; 
}

export function ProfilePage() {
  const { user, setUser, logout } = useAuthStore();
  
  // Image Preview State
  const [imagePreview, setImagePreview] = useState<string | null>(user?.profile_image ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pf = useForm<ProfileForm>({
    defaultValues: {
      full_name: user?.full_name ?? "",
      email: user?.email ?? "",
      address: user?.address ?? "",
    },
  });
  const pw = useForm<PwdForm>();

  const updateProfile = useMutation({
    mutationFn: async (d: ProfileForm) => {
      // 🛠️ Because we are sending an image, we MUST use FormData
      const formData = new FormData();
      formData.append("full_name", d.full_name);
      if (d.email) formData.append("email", d.email);
      if (d.address) formData.append("address", d.address);
      
      if (d.profile_image && d.profile_image.length > 0) {
        formData.append("profile_image", d.profile_image[0]);
      }

      // We pass formData instead of standard JSON
      return patch<User>("/auth/me", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    onSuccess: (u) => {
      setUser(u);
      setImagePreview(u.profile_image ?? null);
      toast.success("Profile updated successfully");
    },
  });

  const changePwd = useMutation({
    mutationFn: (d: PwdForm) => post<void>("/auth/password/change", d),
    onSuccess: () => {
      toast.success("Password changed. Please sign in again.");
      logout();
      window.location.href = "/login";
    },
  });

  if (!user) return null;

  // Handle Image Change for Preview
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Profile Settings</h1>
        <p className="text-sm text-surface-500 mt-1">Manage your personal information and security settings.</p>
      </header>

      {/* Overview Card */}
      <Card>
        <CardBody className="flex flex-wrap items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center text-white text-2xl font-bold overflow-hidden shadow-md">
            {imagePreview ? (
              <img src={imagePreview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              user.full_name.charAt(0)
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xl font-bold text-surface-900">{user.full_name}</div>
            <div className="text-sm text-surface-500 font-mono">{user.user_id} · {user.mobile}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge tone="info" className="capitalize">{user.role.replace(/_/g, " ")}</Badge>
              <Badge tone={user.status === "active" ? "success" : "warning"} className="capitalize">{user.status}</Badge>
            </div>
          </div>
          <div className="text-right text-xs text-surface-500">
            <div>Entries owned</div>
            <div className="text-2xl font-bold text-surface-900">{user.club_entry_count}</div>
          </div>
        </CardBody>
      </Card>

      {/* Edit Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <UserIcon className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody>
          <form onSubmit={pf.handleSubmit((d) => updateProfile.mutate(d))} className="space-y-5">
            
            {/* Image Upload Area */}
            <div className="flex items-center gap-5 p-4 rounded-xl border border-surface-200 bg-surface-50">
              <div 
                className="relative w-20 h-20 rounded-full border-2 border-brand-200 cursor-pointer overflow-hidden group shadow-sm bg-white"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover group-hover:opacity-60 transition-opacity" />
                ) : (
                  <div className="w-full h-full bg-surface-100 flex items-center justify-center text-surface-400 group-hover:bg-surface-200 transition-colors">
                    <UserIcon className="w-8 h-8" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-surface-900">Profile Picture</h4>
                <p className="text-xs text-surface-500 mt-0.5">Click the avatar to upload a new photo. Max size 2MB.</p>
                {/* Hidden File Input */}
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  {...pf.register("profile_image", {
                    onChange: handleImageChange
                  })}
                  ref={(e) => {
                    pf.register("profile_image").ref(e);
                    // @ts-ignore
                    fileInputRef.current = e;
                  }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Full name" leftIcon={<UserIcon className="h-4 w-4" />}
                      error={pf.formState.errors.full_name?.message}
                      {...pf.register("full_name", { required: "Required" })} />
              <Input label="Mobile" value={user.mobile} disabled leftIcon={<Phone className="h-4 w-4" />}
                      hint="Mobile numbers are immutable. Contact support to change." />
            </div>

            <Input label="Email" type="email" leftIcon={<Mail className="h-4 w-4" />}
                    {...pf.register("email")} />
            
            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">Address</label>
              <textarea
                {...pf.register("address")}
                className="w-full h-24 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
                placeholder="Your full address..."
              />
            </div>

            <div className="flex justify-end pt-2 border-t border-surface-200">
              <Button type="submit" size="lg" loading={updateProfile.isPending}>Save Changes</Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Security Form */}
      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
          <ShieldCheck className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody>
          <form onSubmit={pw.handleSubmit((d) => changePwd.mutate(d))} className="space-y-4">
            <Input label="Current password" type="password" leftIcon={<KeyRound className="h-4 w-4" />}
                    error={pw.formState.errors.old_password?.message}
                    {...pw.register("old_password", { required: "Required" })} />
            
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="New password" type="password" leftIcon={<KeyRound className="h-4 w-4" />}
                      hint="Minimum 6 characters"
                      error={pw.formState.errors.new_password?.message}
                      {...pw.register("new_password", {
                        required: "Required",
                        minLength: { value: 6, message: "Minimum 6 characters" },
                      })} />
              <Input label="Confirm new password" type="password" leftIcon={<KeyRound className="h-4 w-4" />}
                      error={pw.formState.errors.new_password_confirm?.message}
                      {...pw.register("new_password_confirm", {
                        required: "Required",
                        validate: (v) => v === pw.watch("new_password") || "Passwords do not match",
                      })} />
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" variant="danger" loading={changePwd.isPending}>Change Password</Button>
            </div>
            <p className="text-2xs text-surface-500 text-right">
              Changing your password will terminate all active sessions.
            </p>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}